import { FormikValues, useFormikContext } from 'formik';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Select from 'react-select';

import { usePergunta } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Icone from 'Component/Icone';

import Ajuda from './Ajuda';
import { FormContexto } from './Form';
import Grupo from './Grupo';
import Rotulo from './Rotulo';

interface Props {
    ajuda?: string;
    name: string;
    rotulo?: string;
    semMargem?: boolean;
    prefixar?: string | React.ReactElement;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    opcoes?: Array<{ valor?: number | string, rotulo: string }> | any;
    padrao?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    aoAtualizar?: (valor: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    forcarValor?: any;
    busca?: string;
    buscar?: (termo: string) => void;
    buscando?: boolean;
    multiple?: boolean;
    inicial?: string | number | null;
    autoSelecao?: boolean;
}

const Selecao = ({
    ajuda,
    busca,
    buscar,
    buscando = false,
    name,
    rotulo,
    prefixar,
    opcoes,
    padrao = 'Selecione uma opção',
    multiple = false,
    inicial,
    autoSelecao = false,
    ...props
}: Props): React.ReactElement =>
{
    const pergunta = usePergunta();
    const { emLinha, ...contexto } = useContext(FormContexto);
    const [ desabilitado, setDesabilitado ] = useState<boolean>(false);
    const { errors, touched, values, setFieldValue, setFieldTouched, initialValues } = useFormikContext<FormikValues>();
    const [ opts, setOpts ] = useState<{ valor?: number | string, rotulo: string }[]>([]);

    useEffect(() => {
        setOpts([ ...opcoes || [] ]);
    }, [ opcoes, padrao ]);

    useEffect(() => {
        if (props.forcarValor) {
            setFieldValue(name, props.forcarValor, true);
            setDesabilitado(true);
        }
    }, [ name, props.forcarValor, setFieldValue ]);

    useEffect(() => {
        if (name.includes('[_ids]')) {
            const chave = name.replace('[_ids]', '');
            const ids: string[] = [];

            if (values[chave] && Array.isArray(values[chave])) {
                values[chave].map((item: { id: string }) => ids.push(item.id));
            }

            setFieldValue(chave, { _ids: ids }, false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ initialValues ]);

    useEffect(() => {
        if (inicial) {
            setFieldValue(name, inicial, false);
        }
    }, [inicial, name, opts, setFieldValue]);

    useEffect(() => {
        if (autoSelecao && opts.length >= 1) {
            setFieldValue(name, opts[0].valor, true);
        }
    }, [autoSelecao, name, opts, setFieldValue])

    const atualizarOpcoes = useCallback(() => {
        if (buscar) {
            setFieldValue(name, '', false);

            pergunta({
                titulo: 'Buscar registros',
                padrao: busca,
                placeholder: 'Digite o que deseja buscar',
                botao: 'Buscar',
                aoConfirmar: busca => buscar(busca),
            });
        }
    }, [busca, buscar, name, pergunta, setFieldValue]);

    return (
        <Grupo
            semMargem={ emLinha || props.semMargem }
            prefixar={ prefixar }
            emLinha={ emLinha }
            { ...buscar
                ? { acrescentarBotao: (
                    <Botao tema="primary" onClick={ atualizarOpcoes }>
                        <Icone nome="search" />
                    </Botao>
                ) }
                : {}
            }
        >
            <Select
                styles={{
                    container: atual => ({
                        ...atual,
                        border: 'none',
                    }),
                    control: atual => ({
                        ...atual,
                        height: emLinha ? 38 : 58,
                        borderRadius: '0.25rem',
                        ...prefixar ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : {},
                        ...buscar ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 } : {},
                        boxShadow: 'none',
                    }),
                    placeholder: atual => ({
                        ...atual,
                        color: '#212529',
                    }),
                    indicatorSeparator: atual => ({
                        ...atual,
                        display: 'none',
                    }),
                    menu: atual => ({
                        ...atual,
                        zIndex: 5000,
                    }),
                    valueContainer: atual => ({
                        ...atual,
                        paddingTop: emLinha ? 0 : 20,
                    }),
                }}
                className={ `form-react-select ${ errors[name] && touched[name] && 'is-invalid' }` }
                name={ name }
                onChange={ e => {
                    if (props.aoAtualizar) {
                        props.aoAtualizar(e.value);
                    }

                    if (multiple) {
                        const valores: number[] = [];

                        e.map(
                            (opcao: { value: number } | number) => valores.push(
                                typeof opcao === 'object' ? opcao.value : opcao
                            )
                        );

                        setFieldValue(name, valores, true);
                    } else {
                        setFieldValue(name, e.value, true);
                    }
                } }
                onBlur={ () => setFieldTouched(name, true, true) }
                value={
                    !name.includes('[_ids]')
                        ? opts.filter(
                            opcao => (
                                multiple
                                    ? values[name].includes(opcao.valor)
                                    : opcao.valor == values[name]
                            )
                        ).map(opcao => ({
                            value: opcao.valor,
                            label: opcao.rotulo,
                        }))
                        : opts.filter(
                            opcao => values[name.replace('[_ids]', '')]?._ids?.includes(opcao.valor)
                        ).map(opcao => ({
                            value: opcao.valor,
                            label: opcao.rotulo,
                        }))
                        // : values[name.replace('[_ids]', '')]?._ids
                }
                isMulti={ multiple }
                options={ opts.map(opcao => ({
                    value: opcao.valor,
                    label: opcao.rotulo,
                })) }
                placeholder={ padrao }
                isDisabled={ desabilitado || buscando || contexto.desabilitado ? true : false }
            />
            <React.Fragment>
                { rotulo && (
                    <Rotulo name={ name }>
                        { rotulo }
                    </Rotulo>
                ) }
            </React.Fragment>
            <React.Fragment>
                { ajuda && (
                    <Ajuda>{ ajuda }</Ajuda>
                ) }
            </React.Fragment>
            <div className="invalid-tooltip">
                { errors[name] && touched[name] && errors[name] }
            </div>
        </Grupo>
    );
}

export default Selecao;
